import { useEffect, useRef, useState } from "react";
import { GROWTH_REQUESTS } from "../../../config/Endpoints.js";
import { getAuthToken } from "../../../utils/Storage.js";
import { getApi, postApi } from "../../utils/api.ts";
import { Button } from "../../components/ui/button/button.tsx";
import type { GrowthRequest } from "./growth-types.ts";
import LinkifiedText from "./linkified-text.tsx";
import styles from "./request-detail.module.css";

type Question = { id: number; requestId: number; noteType: string; comments: string; employeeName?: string; updatedAt?: string | null };
export default function RequestResponse({ requestId, onQuestion, onSent }: { requestId: number; onQuestion: (question: string) => void; onSent: (request: GrowthRequest) => void }) {
  const [question, setQuestion] = useState<Question | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const lock = useRef(false);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError("");
    getApi<Question[]>(`${GROWTH_REQUESTS}/${requestId}/notes?note_type=REQUEST_INFO`, getAuthToken("accessToken"), "json", controller.signal).then((response) => {
      if (!Array.isArray(response.data)) throw new Error("Could not load the owner's question.");
      const latest = response.data.filter((item) => item.requestId === requestId && item.noteType === "REQUEST_INFO").sort((a, b) => b.id - a.id)[0];
      if (!latest) throw new Error("The owner's question is not available yet.");
      if (!controller.signal.aborted) { setQuestion(latest); onQuestion(latest.comments); }
    }).catch((cause) => { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Could not load the owner's question."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [requestId, attempt, onQuestion]);
  async function send() {
    if (!reply.trim() || !question || lock.current) return;
    lock.current = true; setSaving(true); setSaveError("");
    try {
      const response = await postApi<GrowthRequest>(`${GROWTH_REQUESTS}/${requestId}/actions`, getAuthToken("accessToken"), { growth_status: "BEING_REVIEWED", note_type: "ADD_UPDATE", note: reply.trim() });
      onSent(response.data);
    } catch (cause) { setSaveError(cause instanceof Error ? cause.message : "Could not send your response."); }
    finally { lock.current = false; setSaving(false); }
  }
  return <section className={`${styles.card} ${styles.response}`}><h2>Your response is needed</h2>{loading ? <p role="status">Loading the owner's question...</p> : error ? <><p role="alert">{error}</p><Button onClick={() => setAttempt((value) => value + 1)}>Retry question</Button></> : <>
    <p className={styles.question}><LinkifiedText text={question?.comments || ""} /></p>{question?.employeeName ? <small>Asked by {question.employeeName}</small> : null}
    <form onSubmit={(event) => { event.preventDefault(); void send(); }}><label htmlFor="employee-response">Reply</label><textarea id="employee-response" rows={4} placeholder="Write a short response" value={reply} disabled={saving} onChange={(event) => setReply(event.target.value)} />{saveError ? <p role="alert">{saveError}</p> : null}<Button type="submit" disabled={saving || !reply.trim()}>{saving ? "Sending..." : "Send response"}</Button></form>
  </>}</section>;
}
